import { ComponentWithTooltip } from '@components/ComponentWithTooltip';
import Styles from '@components/EditOtherForm/EditOtherForm.module.css';
import { ControlledSearchableSelectField } from '@components/Form/ControlledSearchableSelectField';
import { ReadField } from '@components/ReadField';
import { RequestStatus } from '@features/request/requestSlice';
import type { CatalogLimitedCostTypeData } from '@gql/graphql';
import { cnMixSpace, IconQuestion } from '@uikit';

import type { CSSProperties } from 'react';
import React, { useEffect, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { useBimGetCalculationLimitedCostTypes } from './hooks/useBimGetCalculationLimitedCostTypes';

type TBimLimitedCostTypeSelect = {
    control: Control;
    calculationId?: number;
    isDisabled?: boolean | undefined;
    isTooltipHidden?: boolean | undefined;
    id?: number;
    isViewMode?: boolean;
};

const starColor: CSSProperties = { color: 'var(--color-bg-alert)' };
const tooltipMargin: CSSProperties = { marginLeft: '2px' };

const limitedCostTypeSelectLabel = (required?: boolean) => (
    <ComponentWithTooltip
        tooltipText="Расширение списка типа лимитированных доступно в справочнике переменных для формул прочих и лимитированных затрат"
        className={`${Styles.centeredLabel}`}
        styles={tooltipMargin}
    >
        {required && <span style={starColor}>*</span>}
        <IconQuestion view="secondary" size="s" className={cnMixSpace({ mL: 'xs' })} />
    </ComponentWithTooltip>
);

const getItemLabel = (item: CatalogLimitedCostTypeData) => item.name || '';

export const BimLimitedCostTypeSelect = (props: TBimLimitedCostTypeSelect) => {
    const { isTooltipHidden, control, isDisabled, id, isViewMode, calculationId } = props;

    const { items, status, onNextPageRequest, requestSearchByName, hasNextPage } = useBimGetCalculationLimitedCostTypes(
        {
            calculationId,
            limitedId: id,
            withEffect: false,
        },
    );

    const {
        clearErrors,
        getValues,
        formState: { errors },
    } = useFormContext();

    const selectItems = useMemo(() => items?.map((item) => ({ label: item.name, ...item })), [items]);

    const currentLimitedCostValue = getValues('limitedCostType');

    useEffect(() => {
        if (errors?.limitedCostType?.type === 'required' && currentLimitedCostValue) {
            clearErrors('limitedCostType');
        }
    }, [clearErrors, currentLimitedCostValue, errors?.limitedCostType?.type]);

    return isViewMode ? (
        <ReadField
            value={getValues('limitedCostType')?.name}
            label="Тип лимитированных"
            labelRightSide={limitedCostTypeSelectLabel()}
        />
    ) : (
        <ControlledSearchableSelectField
            name="limitedCostType"
            label="Тип лимитированных"
            control={control}
            items={selectItems}
            labelIcon={limitedCostTypeSelectLabel.bind(null, true) as any}
            className="labelWithEndingIcon"
            disabled={isDisabled}
            rules={{ required: 'Обязательное поле' }}
            labelPosition="left"
            isTooltipHidden={isTooltipHidden}
            direction="rightCenter"
            getItemLabel={getItemLabel}
            onSearch={requestSearchByName}
            onNextPageRequest={onNextPageRequest}
            isLoading={status === RequestStatus.FETCHING}
            hasNextPage={hasNextPage}
            isLazyLoadEnabled
        />
    );
};

BimLimitedCostTypeSelect.displayName = 'BimLimitedCostTypeSelect';
